// @ts-strict-ignore
import React, { useContext, useEffect, useRef } from 'react';
import { TextField, TextArea, useUniqueId } from '@usealloy/component-library';
import { StyledAggregationNameDescriptionContainer } from './styles';
import { AggregationEditorContext } from './context';
import { AGGREGATION_NAME_MAX_LENGTH } from './constants';
import type { AggregationSourceData } from './types';

type NameDescriptionKey = keyof Pick<AggregationSourceData, 'name' | 'description'>;

const AggregationNameAndDescription = () => {
  const {
    sourceData,
    setSourceData,
    validationErrors,
    keyboardFocus,
  } = useContext(AggregationEditorContext);

  const handleChange = (field: NameDescriptionKey) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSourceData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const nameRef = useRef<HTMLInputElement>(null);
  const nameId = useUniqueId('aggregation-name-field');

  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const descriptionId = useUniqueId('aggregation-description-field');

  useEffect(() => {
    keyboardFocus?.register({ id: nameId, ref: nameRef });
    keyboardFocus?.register({ id: descriptionId, ref: descriptionRef });

    return () => {
      keyboardFocus?.unregister(nameId);
      keyboardFocus?.unregister(descriptionId);
    };
  }, [
    nameId,
    descriptionId,
    descriptionRef,
    nameRef,
    keyboardFocus,
  ]);

  return (
    <StyledAggregationNameDescriptionContainer>
      <TextField
        ref={nameRef}
        label="Aggregation Name"
        value={sourceData.name}
        onChange={handleChange('name')}
        type="text"
        required
        maxLength={AGGREGATION_NAME_MAX_LENGTH}
        error={validationErrors.name}
      />
      <TextArea
        ref={descriptionRef}
        label="Description"
        value={sourceData.description}
        onChange={handleChange('description')}
        rows={3}
        required
        placeholder="Enter a description for your aggregation."
        error={validationErrors.description}
      />
    </StyledAggregationNameDescriptionContainer>
  );
};

export default AggregationNameAndDescription;
